import { ArrowUpRight, Circle, Database, ShieldCheck, Square } from 'lucide-react'
import { SiteLayout } from '@/components/site/SiteLayout'

type LayerGeometry = 'polygon' | 'point'

interface LayerRef {
  /**
   * The layer_id as returned by /v1/location, plus any short
   * qualifier (e.g. 'U.S. row, derived'). Free-text inside parens
   * is preserved as a note; the leading identifier is the API key.
   */
  id: string
  /**
   * Underlying geometry of the rows the API returns for this layer.
   * polygon → /v1/location does ST_Contains containment; the hit's
   *           `nearest` block carries distance to the polygon's edge
   *           plus `across` (the same-layer feature over that edge).
   * point   → nearest-1-per-layer KNN, returned in the bottom
   *           `nearest.features[]` with point-to-point distance to the
   *           feature itself. (Use /v1/nearest to query any layer's
   *           closest feature regardless of containment.)
   */
  geometry: LayerGeometry
  /** Optional note rendered in muted text after the id. */
  note?: string
}

// =============================================================================
// /data: Data sources catalog.
//
// Every boundary GeoBorder returns is sourced from a single authoritative,
// publicly-available dataset. This page documents each source: who publishes
// it, what vintage / version we load, what license it carries, what layer it
// powers, and how GeoBorder applies it inside the geocoder.
//
// Keep the entries below in sync with the /v1/sources endpoint output and the
// source-attribution fields on /v1/location responses. If a new ingest script
// lands under scripts/data/, add the source here.
// =============================================================================

interface SourceEntry {
  /** Display name as it appears on the source's own publication. */
  name: string
  /** One-line provenance summary (e.g. dataset family + year). */
  subtitle: string
  /** Calendar / version vintage that GeoBorder currently loads. */
  vintage: string
  /** When the publisher released this vintage (for dataset clarity). */
  published: string
  /** License terms, should be quotable from the publisher's own page. */
  license: string
  /** Format we ingest (Shapefile / GeoJSON / etc.). */
  format: string
  /** Permanent download / catalog page on the publisher's own site. */
  url: string
  /** GeoBorder layer ids this source powers, with geometry indicators. */
  layers: ReadonlyArray<LayerRef>
  /** How GeoBorder applies the dataset, written for a careful reader. */
  usage: string
}

interface SourceGroup {
  org: string
  intro: string
  sources: ReadonlyArray<SourceEntry>
}

const GROUPS: ReadonlyArray<SourceGroup> = [
  {
    org: 'International boundary survey commissions',
    intro:
      'The precise line of the U.S. land borders is defined by two binational survey commissions: the International Boundary and Water Commission (IBWC) for the U.S.-Mexico boundary and the International Boundary Commission (IBC) for the U.S.-Canada boundary. This is the highest-resolution U.S. land boundary published by official U.S. government sources, accurate to the surveyed monument and river-centerline positions. GeoBorder loads the published geometry with source vertices unmodified and composes them into the definitive U.S. border (see Derived datasets).',
    sources: [
      {
        name: 'IBWC, U.S./Mexico International Boundary',
        subtitle:
          'Survey-grade land-monument and Rio Grande / Colorado river-centerline boundary.',
        vintage: 'LSIB 11.4',
        published: '2024',
        license: 'U.S. government work, public domain',
        format: 'ArcGIS FeatureServer (GeoJSON), NAD83(CORS96) Epoch 2002',
        url: 'https://www.ibwc.gov/gis-program/',
        layers: [
          { id: 'in_united_states', geometry: 'polygon', note: 'response field' },
          { id: 'distance_to_us_border', geometry: 'polygon', note: 'response field' },
        ],
        usage:
          "Loaded verbatim from source (land monuments, river centerline, and maritime segments). The land and river segments define the southern U.S. border for the derived definitive_us_border polygon, which drives the `in_united_states` boolean and signed `distance_to_us_border` on every response, and the Roosevelt Reservation strip.",
      },
      {
        name: 'IBC, U.S./Canada International Boundary (v1.3)',
        subtitle:
          'Digital boundary from NAD83 monument and turning-point positions, including the Great Lakes water boundary and the Alaska-Canada line.',
        vintage: 'v1.3',
        published: '2024',
        license: 'U.S. government work, public domain',
        format: 'Shapefile (NAD83)',
        url: 'https://www.internationalboundarycommission.org/en/maps-coordinates/coordinates.php',
        layers: [
          { id: 'in_united_states', geometry: 'polygon', note: 'response field' },
          { id: 'distance_to_us_border', geometry: 'polygon', note: 'response field' },
        ],
        usage:
          'Loaded verbatim from source. Defines the northern U.S. border for the derived definitive_us_border polygon.',
      },
    ],
  },
  {
    org: 'U.S. Census Bureau',
    intro:
      "The Census Bureau publishes the canonical boundary geometries for every U.S. legal and statistical area. GeoBorder loads from two complementary Census releases: the high-precision TIGER/Line file (for in-app spatial work) and the Cartographic Boundary series (for cartographic-quality, generalized renderings of congressional districts). Both are U.S. government works in the public domain.",
    sources: [
      {
        name: 'TIGER/Line 2024, States',
        subtitle: 'All 50 states plus the District of Columbia and U.S. territories.',
        vintage: '2024',
        published: '2024',
        license: 'U.S. government work, public domain',
        format: 'Shapefile (1:24,000 native scale)',
        url: 'https://www.census.gov/geographies/mapping-files/time-series/geo/tiger-line-file.html',
        layers: [
          { id: 'state', geometry: 'polygon' },
          { id: 'country', geometry: 'polygon', note: 'U.S. row, derived' },
        ],
        usage:
          "Loaded for every Census-designated state and equivalent (50 states, D.C., Puerto Rico, U.S. Virgin Islands, Guam, American Samoa, Northern Mariana Islands). Returned directly as the `state` layer. All state polygons are also dissolved into a single U.S. national boundary that powers both in-country detection and the nearest_usa_border distance field on every coordinate response, so the U.S. row in the country layer carries the same geometry as the state borders, with no map seams.",
      },
      {
        name: 'TIGER/Line 2024, Counties',
        subtitle:
          "Counties and county-equivalents (parishes, boroughs, census areas).",
        vintage: '2024',
        published: '2024',
        license: 'U.S. government work, public domain',
        format: 'Shapefile (1:24,000 native scale)',
        url: 'https://www.census.gov/geographies/mapping-files/time-series/geo/tiger-line-file.html',
        layers: [{ id: 'county', geometry: 'polygon' }],
        usage:
          "Loaded for every U.S. county-equivalent. Returned as the `county` layer. Stable identifiers carry the standard 5-digit GEOID (state FIPS + county FIPS), so downstream joins to BLS, IRS, USDA, or other federal datasets work without translation.",
      },
      {
        name: 'TIGER/Line 2024, Places',
        subtitle:
          'Incorporated cities, towns, villages, and Census Designated Places (CDPs).',
        vintage: '2024',
        published: '2024',
        license: 'U.S. government work, public domain',
        format: 'Shapefile (1:24,000 native scale)',
        url: 'https://www.census.gov/geographies/mapping-files/time-series/geo/tiger-line-file.html',
        layers: [{ id: 'place', geometry: 'polygon' }],
        usage:
          "Loaded as the `place` layer. Not every U.S. coordinate falls inside an incorporated place; unincorporated rural land returns no place hit. Place identifiers carry the Census Bureau's PLACEFP (state FIPS + 5-digit place code).",
      },
      {
        name: 'TIGER/Line 2024, ZIP Code Tabulation Areas (ZCTA5)',
        subtitle:
          "Census-built statistical approximations of USPS ZIP code delivery areas.",
        vintage: '2024',
        published: '2024',
        license: 'U.S. government work, public domain',
        format: 'Shapefile (full TIGER/Line precision)',
        url: 'https://www.census.gov/geographies/mapping-files/time-series/geo/tiger-line-file.html',
        layers: [{ id: 'zcta', geometry: 'polygon' }],
        usage:
          "Returned as the `zcta` layer for any U.S. coordinate inside a ZCTA polygon (~33,800 nationwide, covering most populated land). ZCTAs are a Census statistical approximation of USPS ZIP code delivery areas, not the official USPS boundary, which the Postal Service doesn't publish as a geography. The Census builds ZCTAs by aggregating the most-common ZIP code among the addresses inside each Census block. The result panel label reads \"Zip\" as the colloquial name; the layer id stays `zcta`.",
      },
      {
        name: 'TIGER/Line 2024, Core Based Statistical Areas (CBSA)',
        subtitle:
          'OMB-defined metropolitan and micropolitan statistical areas.',
        vintage: '2024',
        published: '2024',
        license: 'U.S. government work, public domain',
        format: 'Shapefile (1:24,000 native scale)',
        url: 'https://www.census.gov/geographies/mapping-files/time-series/geo/tiger-line-file.html',
        layers: [{ id: 'cbsa', geometry: 'polygon' }],
        usage:
          "Returned as the `cbsa` layer for any coordinate inside an OMB-defined metropolitan (≥50,000 population) or micropolitan (≥10,000 population) statistical area. CBSAs are the federal standard for economic functional regions: the unit BLS uses for metro labor-market reporting and HUD uses for fair-market-rent areas.",
      },
      {
        name: 'Cartographic Boundary 2024, 119th Congressional Districts (1:500k)',
        subtitle:
          'Generalized congressional district geometries for the 119th Congress.',
        vintage: '2024 / 119th Congress',
        published: '2024',
        license: 'U.S. government work, public domain',
        format: 'Shapefile (cartographic, 1:500,000 generalization)',
        url: 'https://www.census.gov/geographies/mapping-files/time-series/geo/cartographic-boundary.html',
        layers: [{ id: 'congressional_district', geometry: 'polygon' }],
        usage:
          "Returned as the `congressional_district` layer. GeoBorder uses the Cartographic Boundary edition (rather than full-precision TIGER) because congressional districts are typically used for political mapping where 1:500,000 generalization matches downstream rendering conventions. Identifiers carry the Census GEOID (state FIPS + 2-digit district number).",
      },
    ],
  },
  {
    org: 'Administrative Office of the U.S. Courts',
    intro:
      'The federal judicial district map is defined directly by U.S. Code (28 U.S.C. §§ 81-131). GeoBorder loads the district boundaries as published by the Administrative Office of the U.S. Courts, the federal agency that maintains the official caselaw infrastructure, and derives circuit court boundaries from them. Within each district, individual court divisions (the courthouse-level units that determine case venue) are derived from each district\'s published Local Rules of Procedure.',
    sources: [
      {
        name: 'U.S. Federal Judicial Districts (2025)',
        subtitle:
          'Boundaries of the 94 federal trial-court districts as established by 28 U.S.C.',
        vintage: '2025',
        published: '2025',
        license: 'U.S. government work, public domain',
        format: 'Shapefile (composed from Census state + county polygons per 28 U.S.C.)',
        url: 'https://www.uscourts.gov/about-federal-courts/federal-courts-public/court-website-links',
        layers: [
          { id: 'federal_judicial_district', geometry: 'polygon' },
          { id: 'federal_circuit_court',     geometry: 'polygon', note: 'derived' },
        ],
        usage:
          "Returned as the `federal_judicial_district` layer (94 U.S. district courts plus the District of Columbia and territorial district courts). The 13 U.S. Circuit Courts of Appeals (`federal_circuit_court` layer) are derived by dissolving the constituent district polygons; there is no separate official polygon for the Ninth Circuit, for example, it is the union of its 15 district courts. Each district hit carries the statute_url referencing the section of 28 U.S.C. that defines it.",
      },
      {
        name: 'U.S. Federal Court Divisions (28 U.S.C. §§ 81-131 + Local Rules)',
        subtitle:
          'Sub-district court divisions, the courthouse-level units that determine where a case is filed and tried.',
        vintage: '2025',
        published: 'Per district',
        license: 'U.S. government work, public domain',
        format: 'Derived (county-dissolved per 28 U.S.C. + Local Rules)',
        url: 'https://www.law.cornell.edu/uscode/text/28/part-I/chapter-5',
        layers: [{ id: 'federal_judicial_division', geometry: 'polygon' }],
        usage:
          "Returned as the `federal_judicial_division` layer for any coordinate inside a district that has formal divisions. Division geometry is the dissolved union of the constituent county polygons. Coverage spans 30 states and 209 (district, division) tuples: 17 states whose divisions are statutorily defined in 28 U.S.C. §§ 81-131 (Alabama, Arkansas, California, Georgia, Illinois Northern, Indiana, Iowa, Maryland, Michigan, Minnesota, Mississippi, Missouri, Ohio, South Carolina, South Dakota, Tennessee, Texas, Utah) plus 13 states whose divisions live in each district's published Local Rules of Procedure (Arizona, Florida Middle + Northern, Illinois Central + Southern, New Jersey, Massachusetts, Connecticut, Oregon, Maine, Nevada, Kentucky Eastern + Western, Washington Western, West Virginia Northern + Southern, Wisconsin Eastern, Oklahoma Western). States or districts with single-courthouse coverage (e.g., New York Southern, Virginia Eastern, Pennsylvania Eastern, Washington Eastern, Wisconsin Western, Oklahoma Northern + Eastern, Vermont) have no formal divisions, so they return no `federal_judicial_division` hit, which is correct per source material. Case venue and trial location follow the division of the underlying event; the primary use case is arrest-location to court-jurisdiction lookups.",
      },
    ],
  },
  {
    org: 'U.S. Department of Justice',
    intro:
      'Immigration court locations come from EOIR, the Executive Office for Immigration Review, the DOJ agency that adjudicates removal proceedings. Each court is a point feature returned alongside the polygon-based judicial hits.',
    sources: [
      {
        name: 'EOIR Immigration Court Locations (2025)',
        subtitle:
          'Official roster of the ~65 active immigration courts nationwide.',
        vintage: '2025',
        published: 'Updated continuously by DOJ',
        license: 'U.S. government work, public domain',
        format: 'Scraped (court name + address) + geocoded (Census Geocoder + OSM Nominatim fallback)',
        url: 'https://www.justice.gov/eoir/eoir-immigration-court-listing',
        layers: [{ id: 'federal_immigration_court', geometry: 'point' }],
        usage:
          "Returned for any U.S. coordinate in the response's bottom `nearest.features[]` as the nearest `federal_immigration_court`: point-to-point distance and bearing from the input to the nearest court, not a containment polygon. Also queryable directly via /v1/nearest. EOIR does not publish jurisdictional polygons (8 C.F.R. § 1003.11 keys cases to the DHS detention facility or district office, not the respondent's residence), so nearest court is the practical answer for a coordinate lookup. Court names, addresses, and per-row source URLs are recorded for auditability.",
      },
    ],
  },
  {
    org: 'U.S. Immigration and Customs Enforcement',
    intro:
      'ERO (Enforcement and Removal Operations) field offices are the 25 nationwide ICE offices that handle civil immigration enforcement and detention coordination. Returned as a point layer alongside the polygon-based admin hits.',
    sources: [
      {
        name: 'ICE ERO Field Offices (FY 2023 roster)',
        subtitle:
          '25 field offices per the FY 2023 ERO Field Offices PDF map.',
        vintage: '2023',
        published: 'February 2024 (last map update)',
        license: 'U.S. government work, public domain',
        format: 'Scraped (office name + address) + geocoded (Census Geocoder + OSM Nominatim fallback)',
        url: 'https://www.ice.gov/doclib/about/offices/ero/pdf/eroFieldOffices.pdf',
        layers: [{ id: 'ice_ero_field_office', geometry: 'point' }],
        usage:
          "Returned for any U.S. coordinate in the bottom `nearest.features[]` as the nearest `ice_ero_field_office`: point-to-point distance and bearing to the nearest field office. Also queryable via /v1/nearest. ICE publishes Area of Responsibility descriptions in mixed-precision text ('upstate New York' alongside whole-state or whole-county references) that don't resolve to a clean polygon, so the layer ships as points only. Office names and addresses are recorded for auditability.",
      },
    ],
  },
  {
    org: 'U.S. Customs and Border Protection',
    intro:
      'CBP operates the U.S. Ports of Entry, the official land, air, and sea border crossings where travelers and goods enter the country. Each POE carries a 4-digit CBP port code used on apprehension paperwork and manifest filings; the layer returns the nearest POE for any U.S. coordinate.',
    sources: [
      {
        name: 'CBP Ports of Entry (2025 roster)',
        subtitle:
          '317 active U.S. POEs scraped from cbp.gov per-state roster + per-POE detail pages.',
        vintage: '2025',
        published: 'Updated continuously by CBP',
        license: 'U.S. government work, public domain',
        format: 'Scraped (name + address + 4-digit port code) + geocoded (Census Geocoder + OSM Nominatim fallback)',
        url: 'https://www.cbp.gov/contact/ports',
        layers: [{ id: 'federal_port_of_entry', geometry: 'point' }],
        usage:
          "Returned for any U.S. coordinate in the bottom `nearest.features[]` as the nearest `federal_port_of_entry`: point-to-point distance and bearing to the nearest POE. Also queryable via /v1/nearest. Each entry carries the 4-digit CBP port code in `identifiers.port_code`, the canonical key CBP uses for apprehensions, OFO and USBP location codes, and manifest filings. Land POEs often have descriptive addresses (e.g., Sasabe's 'Highway 286 and International Border'); those geocode via OSM Nominatim's city-level fallback when the Census Geocoder doesn't match, which lands the coordinate at the right town, within a few hundred meters of the actual crossing. The per-row geocoder is recorded in `properties.geocoder` for auditability.",
      },
    ],
  },
  {
    org: 'U.S. Geological Survey',
    intro:
      'Federal land ownership and surface management (BLM, USFS, NPS, FWS, USACE, Bureau of Reclamation, DOE, and DoD-managed sites) comes from a single USGS source: the Protected Areas Database of the United States (PAD-US). The Federal Fee Managers Authoritative subset provides per-polygon agency attribution, unit names (e.g., "Tonto National Forest"), and designation types in one consolidated feed maintained by the USGS Gap Analysis Project. GeoBorder keys on the land manager, which USGS recommends over owner for land-management questions.',
    sources: [
      {
        name: 'PAD-US 4.1, Federal Fee Managers Authoritative',
        subtitle:
          'USGS-published federal land-manager inventory: BLM, USFS, NPS, FWS, USACE, Bureau of Reclamation, and DOE, keyed on land manager.',
        vintage: '4.1',
        published: 'December 2024',
        license: 'U.S. government work, public domain',
        format: 'File geodatabase (local PAD-US 4.1 GDB extract)',
        url: 'https://www.usgs.gov/data/protected-areas-database-united-states-pad-us-4',
        layers: [
          { id: 'federal_blm',      geometry: 'polygon' },
          { id: 'federal_usfs',     geometry: 'polygon' },
          { id: 'federal_nps',      geometry: 'polygon' },
          { id: 'federal_fws',      geometry: 'polygon' },
          { id: 'federal_usace',    geometry: 'polygon' },
          { id: 'federal_usbr',     geometry: 'polygon' },
          { id: 'federal_doe',      geometry: 'polygon' },
          { id: 'federal_military', geometry: 'polygon', note: 'military designation' },
          { id: 'federal_land',     geometry: 'polygon', note: 'derived, dissolved' },
        ],
        usage:
          "Each PAD-US Fee polygon loads into one of seven manager-keyed GeoBorder layers (BLM, USFS, NPS, FWS, USACE, USBR, DOE), which USGS recommends over owner for land-management questions, plus military by designation. Geometry is loaded from the published PAD-US 4.1 file geodatabase and validated on load. The `federal_land` layer is derived at query time, emitted whenever any agency-specific federal layer matched, with the closest per-agency edge supplying its `nearest` block. Hit identifiers carry the PAD-US source id, owner, manager, designation, unit name, and state name so consumers can match against PAD-US source data without a second round trip. Two normalizations are applied on top of the source. PAD-US ships several overlapping polygon records for some units, so a coordinate could match the same refuge or forest more than once; responses return one hit per unit, keeping the largest polygon. And because BLM's undesignated public land has no parcel name, PAD-US labels it with the administering office; those rows are named \u201cBLM Public Lands (\u2026 Field Office)\u201d so they do not read as though an office building were the federal land. The raw PAD-US string stays in the hit's `unit_name` identifier.",
      },
    ],
  },
  {
    org: 'U.S. Bureau of Indian Affairs',
    intro:
      'Tribal trust land (American Indian and Alaska Native land held in trust or restricted-fee status by the United States for federally recognized tribes) comes from BIA directly. It is not included in PAD-US Federal Fee Managers, so it is a separate source.',
    sources: [
      {
        name: 'BIA AIAN National Land Area Representation (AIAN-LAR)',
        subtitle:
          "BIA's authoritative tribal trust polygon dataset, sourced from official land title records.",
        vintage: '2025',
        published: 'Updated continuously by BIA',
        license: 'U.S. government work, public domain',
        format: 'ArcGIS REST MapServer (GeoJSON)',
        url: 'https://biamaps.geoplatform.gov/server/rest/services/DivLTR/BIA_AIAN_National_LAR/MapServer',
        layers: [{ id: 'federal_bia_trust', geometry: 'polygon' }],
        usage:
          "Returned as the `federal_bia_trust` layer for any coordinate inside an AIAN-LAR polygon (~335 entries nationally). Maintained by BIA's Branch of Geospatial Support from treaties, statutes, agreements, executive orders, and deeds, the same legal record corpus that defines trust status. Each hit carries the BIA `lar_id` and `lar_name` in identifiers; `classification` distinguishes trust from restricted-fee where the BIA captures it.",
      },
    ],
  },
  {
    org: 'Natural Earth',
    intro:
      'For points outside the United States, GeoBorder uses Natural Earth, the de-facto standard public-domain dataset for world-scale boundary cartography, maintained by a community of professional cartographers led by Tom Patterson (formerly of the U.S. National Park Service) and Nathaniel Vaughn Kelso.',
    sources: [
      {
        name: 'Admin 0 Countries (1:50m)',
        subtitle:
          'Sovereign-state boundaries at 1:50,000,000 scale, v5.1.2.',
        vintage: '5.1.2',
        published: '2022',
        license: 'Public Domain',
        format: 'Shapefile',
        url: 'https://www.naturalearthdata.com/downloads/50m-cultural-vectors/',
        layers: [{ id: 'country', geometry: 'polygon', note: 'non-U.S. only' }],
        usage:
          "Used as the source for the `country` layer for points outside the United States. For any in-U.S. coordinate, the country hit is derived from the Census state-dissolved boundary instead (see U.S. Census Bureau section above), so U.S. responses carry full government-grade precision, while international coordinates resolve to a country with consistent ISO alpha-2 and alpha-3 identifiers from Natural Earth's metadata.",
      },
    ],
  },
  {
    org: 'Derived datasets',
    intro:
      'These geometries are computed inside GeoBorder from the authoritative sources above, rather than ingested as separate files. They exist so the API can answer questions that no single published dataset directly addresses; every derived geometry inherits the license and provenance of the source it was computed from.',
    sources: [
      {
        name: 'Definitive U.S. Border (survey lines grafted onto the national coastline)',
        subtitle:
          'The authoritative survey-grade U.S. polygon: IBWC + IBC boundary lines stitched to the TIGER national coastline.',
        vintage: 'IBWC LSIB 11.4 + IBC v1.3 + TIGER 2024 coast',
        published: 'Derived',
        license: 'Inherits IBWC, IBC, and Census TIGER: U.S. government works, public domain',
        format: 'PostGIS geometry',
        url: 'https://www.ibwc.gov/gis-program/',
        layers: [
          { id: 'in_united_states', geometry: 'polygon', note: 'response field, all responses' },
          { id: 'distance_to_us_border', geometry: 'polygon', note: 'response field, all responses' },
        ],
        usage:
          "Computed by grafting the IBWC (Mexico) and IBC (Canada) survey boundary lines onto the Census national coastline and merging the result into a single polygon; the raw source data is never modified. This polygon is the source of truth for U.S. membership: it powers the `in_united_states` boolean and the signed `distance_to_us_border` on every response, and (with `strict_us_border=true`) gates which jurisdiction layers are returned. Its land and river edges follow the survey lines, so classification is accurate to the surveyed boundary.",
      },
      {
        name: 'U.S. National Boundary (dissolved from Census TIGER states)',
        subtitle:
          'A single polygon representing the United States, derived by union of all state, D.C., and territorial polygons.',
        vintage: '2024 (matches TIGER/Line states)',
        published: 'Derived',
        license: 'Inherits Census TIGER: U.S. government work, public domain',
        format: 'PostGIS geometry',
        url: 'https://www.census.gov/geographies/mapping-files/time-series/geo/tiger-line-file.html',
        layers: [
          { id: 'country', geometry: 'polygon', note: 'U.S. row' },
          { id: 'nearest_usa_border', geometry: 'polygon', note: 'response field, all responses' },
        ],
        usage:
          "Computed once during ingest by dissolving every state polygon in the TIGER/Line 2024 states data. The result is an aligned U.S. national outline with no map seams where the country boundary coincides with a state border. This geometry powers two response fields: the U.S. row in the country layer (so the in-country attribution carries full Census precision), and the `nearest_usa_border` distance computed on every API response (so an Ottawa or Mexico City coordinate carries a meaningful distance-to-the-U.S.-border value rather than a fall-through to the nearest Natural Earth edge).",
      },
      {
        name: 'Roosevelt Reservation (60-ft border strip, derived)',
        subtitle:
          'The 60-foot federal strip along the U.S.-Mexico border in CA, AZ, and NM, measured off the IBWC survey line.',
        vintage: '1907',
        published: 'Presidential Proclamation, May 27, 1907 (35 Stat. 2136)',
        license: 'U.S. government work, public domain (derivation released under the same terms)',
        format: 'PostGIS geometry (60-foot strip along the IBWC survey line)',
        url: 'https://www.ibwc.gov/gis-program/',
        layers: [{ id: 'federal_roosevelt_reservation', geometry: 'polygon', note: 'derived' }],
        usage:
          "Returned as the `federal_roosevelt_reservation` layer for any coordinate inside the 60-foot strip. Because the strip is measured along the border, its accuracy is inherited from the border line, so it is derived from the IBWC U.S.-Mexico boundary (land monuments and river centerline) rather than a generalized boundary. The IBWC segments near CA, AZ, and NM are selected (Texas is excluded, because Texas retained its public lands at admission, so the 1907 proclamation never transferred Texas-side border land), extended 60 feet (18.288 m) from the line, and clipped to the definitive U.S. border so only the U.S. side remains.",
      },
    ],
  },
]

export function DataPage() {
  return (
    <SiteLayout>
      {/* Hero band */}
      <div className="border-b border-[var(--color-border)] bg-[var(--color-surface)]">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16">
          <span className="inline-block text-[11px] font-medium uppercase tracking-[0.18em] text-[var(--color-accent)] font-[var(--font-mono)]">
            Data
          </span>
          <h1 className="mt-2 text-[28px] font-semibold tracking-tight text-[var(--color-text-strong)] sm:text-[32px]">
            Authoritative data sources
          </h1>
          <p className="mt-4 text-[15px] leading-relaxed text-[var(--color-text)]">
            GeoBorder is built exclusively on authoritative, publicly-available
            government datasets and openly-licensed cartographic data. Every
            boundary returned by the API carries source attribution back to its
            publisher, visible in the <code className="font-[var(--font-mono)] text-[13.5px] text-[var(--color-accent)]">source</code> field on every <code className="font-[var(--font-mono)] text-[13.5px] text-[var(--color-accent)]">/v1/location</code> hit and listed in full on <code className="font-[var(--font-mono)] text-[13.5px] text-[var(--color-accent)]">/v1/sources</code>.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-full border border-[var(--color-border)] bg-[var(--color-elev)] px-3 py-1.5 text-[12.5px] text-[var(--color-text)]">
              <ShieldCheck size={14} className="text-[var(--color-accent)]" aria-hidden />
              U.S. government works
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-[var(--color-border)] bg-[var(--color-elev)] px-3 py-1.5 text-[12.5px] text-[var(--color-text)]">
              <ShieldCheck size={14} className="text-[var(--color-accent)]" aria-hidden />
              Public domain
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-[var(--color-border)] bg-[var(--color-elev)] px-3 py-1.5 text-[12.5px] text-[var(--color-text)]">
              <Database size={14} className="text-[var(--color-accent)]" aria-hidden />
              No proprietary feeds
            </span>
          </div>
        </div>
      </div>

      {/* Source groups */}
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16">
        <div className="space-y-16">
          {GROUPS.map((group) => (
            <section key={group.org}>
              <header className="mb-6">
                <h2 className="text-[22px] font-semibold tracking-tight text-[var(--color-text-strong)]">
                  {group.org}
                </h2>
                <p className="mt-3 text-[14.5px] leading-relaxed text-[var(--color-text)]">
                  {group.intro}
                </p>
              </header>

              <div className="space-y-5">
                {group.sources.map((source) => (
                  <SourceCard key={source.name} source={source} />
                ))}
              </div>
            </section>
          ))}

          {/* Closing: methodology */}
          <section>
            <header className="mb-6">
              <h2 className="text-[22px] font-semibold tracking-tight text-[var(--color-text-strong)]">
                Provenance and versioning
              </h2>
              <p className="mt-3 text-[14.5px] leading-relaxed text-[var(--color-text)]">
                Every boundary is stored with the
                <code className="mx-1 font-[var(--font-mono)] text-[13.5px] text-[var(--color-accent)]">source_id</code>
                of the dataset it came from, the
                <code className="mx-1 font-[var(--font-mono)] text-[13.5px] text-[var(--color-accent)]">vintage</code>
                (calendar year or version number), and the
                <code className="mx-1 font-[var(--font-mono)] text-[13.5px] text-[var(--color-accent)]">loaded_at</code>
                timestamp from the ingest run. The same fields surface on every
                API response, so consumers can verify exactly which release of
                a dataset they're reading without an out-of-band check.
              </p>
              <p className="mt-3 text-[14.5px] leading-relaxed text-[var(--color-text)]">
                GeoBorder never fetches external data at request time. All
                lookups run against locally-ingested data, so an API response
                is deterministic given a fixed data version. That is also
                what makes the
                <code className="mx-1 font-[var(--font-mono)] text-[13.5px] text-[var(--color-accent)]">ETag</code>
                and conditional-request flow on
                <code className="mx-1 font-[var(--font-mono)] text-[13.5px] text-[var(--color-accent)]">/v1/location</code>
                possible.
              </p>
            </header>
          </section>
        </div>
      </div>
    </SiteLayout>
  )
}

function SourceCard({ source }: { source: SourceEntry }) {
  return (
    <article className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-elev)] p-6 sm:p-7">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h3 className="text-[17px] font-semibold tracking-tight text-[var(--color-text-strong)]">
            {source.name}
          </h3>
          <p className="mt-1.5 text-[13.5px] leading-relaxed text-[var(--color-muted)]">
            {source.subtitle}
          </p>
        </div>
        <a
          href={source.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 self-start rounded-full border border-[var(--color-accent)]/40 bg-[var(--color-accent-soft)]/40 px-3.5 py-1.5 text-[12.5px] font-medium text-[var(--color-accent)] transition-colors hover:border-[var(--color-accent)] hover:bg-[var(--color-accent-soft)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-elev)]"
        >
          View source
          <ArrowUpRight size={13} aria-hidden />
        </a>
      </div>

      <dl className="mt-5 grid grid-cols-2 gap-x-6 gap-y-4 border-y border-[var(--color-border)] py-5 sm:grid-cols-4">
        <Field label="Vintage" value={source.vintage} />
        <Field label="Published" value={source.published} />
        <Field label="License" value={source.license} />
        <Field label="Format" value={source.format} />
      </dl>

      <div className="mt-5">
        <p className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-[var(--color-muted)] font-[var(--font-mono)]">
          Powers these layers
        </p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {source.layers.map((layer) => (
            <LayerChip key={`${layer.id}-${layer.note ?? ''}`} layer={layer} />
          ))}
        </div>
      </div>

      <p className="mt-5 text-[14px] leading-relaxed text-[var(--color-text)]">
        {source.usage}
      </p>
    </article>
  )
}

/**
 * Layer identifier + a small geometry indicator. The geometry pill
 * tells consumers at a glance whether the API hit for this layer is
 * polygon containment (ST_Contains, hit's `nearest` = distance to the
 * polygon edge + `across`) or a point KNN (nearest-1, surfaced in the
 * response's bottom `nearest.features[]` as point-to-point distance to
 * the feature itself). Optional `note`
 * renders muted after the id for qualifiers like "derived" or
 * "U.S. row, derived".
 */
function LayerChip({ layer }: { layer: LayerRef }) {
  const isPoint = layer.geometry === 'point'
  const Icon = isPoint ? Circle : Square
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] pl-2 pr-1.5 py-0.5 text-[11.5px] font-[var(--font-mono)] text-[var(--color-text)]">
      <span>{layer.id}</span>
      {layer.note && (
        <span className="text-[var(--color-muted)]">({layer.note})</span>
      )}
      <span
        title={isPoint ? 'Point geometry, nearest feature' : 'Polygon geometry, containment'}
        className={[
          'inline-flex items-center gap-0.5 rounded border px-1 py-px text-[10px] uppercase tracking-[0.05em]',
          isPoint
            ? 'border-[var(--color-accent)]/40 bg-[var(--color-accent-soft)]/50 text-[var(--color-accent)]'
            : 'border-[var(--color-border)] bg-[var(--color-elev)] text-[var(--color-muted)]',
        ].join(' ')}
      >
        <Icon size={9} aria-hidden fill={isPoint ? 'currentColor' : 'none'} strokeWidth={2} />
        <span>{isPoint ? 'Point' : 'Polygon'}</span>
      </span>
    </span>
  )
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-[var(--color-muted)] font-[var(--font-mono)]">
        {label}
      </dt>
      <dd className="mt-1 text-[13px] leading-snug text-[var(--color-text)]">{value}</dd>
    </div>
  )
}
